import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { useUserContext } from "~/hooks/useUserContext";
import { canManageQuizzes } from "~/helpers/quizPermissions";
import HeadingPage from "~/molecules/HeadingPage";
import { Tabs, TabsList, TabsTrigger } from "~/atoms/tabs";

const tabRoutes = {
  mine: "/anchoring/me",
  unanchored: "/anchoring/unanchored",
  anchored: "/anchoring/quizzes",
} as const;

type TabKey = keyof typeof tabRoutes;

const activeTabFromPath = (pathname: string): TabKey => {
  if (pathname.endsWith("/quizzes")) return "anchored";
  if (pathname.endsWith("/unanchored")) return "unanchored";
  return "mine";
};

const AnchoringPage = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const location = useLocation();
  const navigate = useNavigate();

  const isManager = canManageQuizzes(user.role);
  const activeTab = activeTabFromPath(location.pathname);

  // Readers only have the "your quizzes" tab.
  if (!isManager && activeTab !== "mine") {
    return <Navigate to={tabRoutes.mine} replace />;
  }

  return (
    <div className="container space-y-4 py-7">
      <div>
        <HeadingPage title={t(($) => $.anchoring.title)} />
        <p className="text-muted-foreground">
          {t(($) => $.anchoring.description)}
        </p>
      </div>
      <Tabs
        value={activeTab}
        onValueChange={(key) => void navigate(tabRoutes[key as TabKey])}
      >
        <TabsList layout="compact">
          <TabsTrigger value="mine">
            {t(($) => $.anchoring.tabs.mine)}
          </TabsTrigger>
          {isManager && (
            <>
              <TabsTrigger value="unanchored">
                {t(($) => $.anchoring.tabs.unanchored)}
              </TabsTrigger>
              <TabsTrigger value="anchored">
                {t(($) => $.anchoring.tabs.anchored)}
              </TabsTrigger>
            </>
          )}
        </TabsList>
        <div className="bg-card flex-1 rounded-b-lg border-r border-b border-l px-4 py-6">
          <Outlet />
        </div>
      </Tabs>
    </div>
  );
};

export default AnchoringPage;
